import { DotNotationFieldRendererViewModel } from '../../../reusableFeatures';
import { IVendor, IVendorData, IVendorSearchGrid, IVendorSearchGridItem } from './types';
import { VendorApi, IVendorViewModel } from '../Api';
import { portalUserService } from '../../UserService';
import { validateData } from '../../../ui';
import { backendServices } from '../..';

function convertToVendor(data: IVendorViewModel): IVendor {
    return {
        id: data.Id,
        vendorNumber: data.VendorNumber ?? '',
        vendorName: data.VendorName ?? '',
        vendorClass: data.VendorClass,
        active: data.Active,
        duns: data.DUNS ?? '',
        annualVolume: data.AnnualVolume,
        annualSpend: data.AnnualSpend,
        taxIdentifier: data.TaxIdentifier ?? '',
        taxIdentifierAuthority: data.TaxIdentifierAuthority ?? '',
        attributes: DotNotationFieldRendererViewModel.fromXMLBasedObject(data.Attributes, {}),
        // default to null if undefined
        orgUnit: data.OrgUnit ?? null,
    };
}

function convertToVendorViewModel(data: IVendorData): Partial<IVendorViewModel> {
    return {
        VendorNumber: data.vendorNumber,
        VendorName: data.vendorName,
        VendorClass: data.vendorClass,
        Active: data.active,
        DUNS: data.duns,
        AnnualVolume: Number(data.annualVolume),
        AnnualSpend: Number(data.annualSpend),
        TaxIdentifier: data.taxIdentifier,
        TaxIdentifierAuthority: data.taxIdentifierAuthority,
        Attributes: data.attributes.state,
        OrgUnit: data.orgUnit,
    };
}

export class VendorService {
    private api = new VendorApi();

    private companyApi = new backendServices.Apis.CompanyApi();

    public getVendor = async (vendorId: number): Promise<IVendor | null> => {
        const response = await this.api.getVendor(vendorId);

        if (!response.data) {
            return null;
        }

        return convertToVendor(response.data);
    };

    private validateVendor = async (vendor: IVendorData) => {
        const childCompanyId = portalUserService.getSelectedCompanyChild()?.value?.toString();

        const { data: vendorClasses } = await this.companyApi.getVendorClass({
            companyId: portalUserService.getCurrentCompanyId(),
            childCompanyId,
        });

        validateData((errors) => {
            if (!vendor.vendorNumber) {
                errors.push('Vendor Number is required');
            }
            if (!vendor.vendorName) {
                errors.push('Vendor Name is required');
            }
            if (!vendor.vendorClass && vendorClasses.length > 0) {
                errors.push('Vendor Class is required');
            }
        });
    };

    public updateVendor = async (vendorId: number, vendor: IVendorData): Promise<void> => {
        await this.validateVendor(vendor);
        await this.api.updateVendor(vendorId, convertToVendorViewModel(vendor));
    };

    public createVendor = async (vendor: IVendorData): Promise<number> => {
        await this.validateVendor(vendor);
        const companyId = portalUserService.getCurrentCompanyId();

        if (!companyId) {
            throw new Error('Cannot create vendor without company id');
        }

        const response = await this.api.createVendor({
            // Do not remove hardcoded params, these are needed for the API to work...
            ExcludeFromDynamicDiscounting: 1,
            MaxEmailSize: 1024,
            CompanyId: companyId,
            ...convertToVendorViewModel(vendor),
        });

        return response.data.IDs[0];
    };

    public getVendorSearchResultsGrid = async (
        params: backendServices.Apis.GetTradingPartnersSearchParams
    ): Promise<IVendorSearchGrid> => {
        const response = await this.companyApi.getTradingPartnersSearch(params);

        const transformItemHelper = (item: backendServices.ViewModels.TradingPartnerSearchViewModel) => {
            if (!item.CompanyID && !item.VendorId) {
                throw new Error(`Expected either vendor or supplier id to be non-null: ${JSON.stringify(item)}`);
            }

            const transformedItem: IVendorSearchGridItem = {
                id: `${item.CompanyID}-${item.VendorId}-${item.VendorSiteNumber}`,
                detailsId: `${item.CompanyID}-${item.VendorId}`,
                buyerCompanyId: item.BuyerCompanyID,
                name: item.Name ?? '',
                vendorNumber: item.VendorNumber,
                vendorClass: item.VendorClass,
                contactName: item.ContactName,
                contactEmail: item.ContactEmail,
                siteNumber: item.VendorSiteNumber,
                invoiceProfileType: item.InvoiceProfileType,
                orgUnit: item.OrgUnit,
                companyId: item.CompanyID ?? undefined,
            };

            return transformedItem;
        };

        return {
            count: response.data.Count,
            items: response.data.Items.map(transformItemHelper),
        };
    };

    static defaultCRUDFields: IVendorData = {
        vendorNumber: '',
        vendorName: '',
        vendorClass: null,
        active: false,
        duns: '',
        annualVolume: null,
        annualSpend: null,
        taxIdentifier: '',
        taxIdentifierAuthority: '',
        attributes: DotNotationFieldRendererViewModel.fromXMLBasedObject(null, {}),
        orgUnit: null,
    };
}
